import * as React from 'react'
import { useMemo } from 'react'
import { Formik, Field } from 'formik'
import Link from 'next/link'
import cn from 'clsx'
import { useTranslations } from 'next-intl'

import { Button, TextInputField, CheckboxInput } from './form'

import { createLoginFormValidation } from '../utils/validation'

type Props = {
  onSubmit: (values: any, actions: any) => void
}

export default function LoginForm({ onSubmit }: Props) {
  const t = useTranslations()
  const validationSchema = useMemo(() => createLoginFormValidation(t), [t])

  return (
    <React.Fragment>
      <Formik
        initialValues={{ email: '', password: '', rememberMe: false }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formProps) => (
          <form
            onSubmit={formProps.handleSubmit}
            noValidate
            className="w-full max-w-[377px] mx-auto lg:max-w-[483px] lg:rounded-md lg:bg-white lg:shadow-[0_10px_20px_rgba(144,144,144,0.1)] lg:px-[53px] lg:pt-[38px] lg:pb-[15px]"
          >
            <h1 className="mb-[34px] text-[24px] leading-[28px] font-bold text-[#14145a] text-left lg:mb-[37px] lg:text-[50px] lg:leading-[58px] lg:text-center">
              {t('loginPage.title')}
            </h1>

            <div className="mb-[10px] lg:mb-[20px]">
              <Field
                name="email"
                type="email"
                label={t('loginPage.form.email')}
                component={TextInputField}
              />
            </div>
            <div className="mb-[10px] lg:mb-[20px]">
              <Field
                name="password"
                type="password"
                label={t('loginPage.form.password')}
                component={TextInputField}
                autoComplete="current-password"
              />
            </div>

            <div className="flex items-center justify-between mt-[17px] mb-[19px] lg:mt-0 lg:mb-[26px]">
              <div className="remember-me flex-1">
                <CheckboxInput name="rememberMe">
                  {t('loginPage.form.rememberMe')}
                </CheckboxInput>
              </div>
              <div className="forgot-password relative -top-[2px] leading-[21px] text-right lg:static flex-1">
                <Link
                  href="/forgot-password"
                  className={cn('text-gray-600 text-[12px] lg:text-[14px]')}
                >
                  {t('authSecondaryActions.forgotPassword')}
                </Link>
              </div>
            </div>

            <div className="flex items-center justify-between mt-[17px] mb-[19px] lg:mt-0 lg:mb-[26px]">
              <div className="secondary-action flex-1">
                <div className="label mb-[-3px] text-[12px] leading-[1] text-input-label">
                  {t('authSecondaryActions.haveNoAccount')}
                </div>
                <Link
                  href="/registration"
                  className={cn('text-gray-600 text-[14px] lg:text-[16px]')}
                >
                  {t('authSecondaryActions.registration')}
                </Link>
              </div>
              <div className="primary-action flex-1 text-right">
                <Button
                  disabled={formProps.isSubmitting}
                  onClick={() => formProps.handleSubmit()}
                  type="submit"
                >
                  {formProps.isSubmitting
                    ? t('loginPage.form.submitting')
                    : t('loginPage.form.submit')}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </React.Fragment>
  )
}
